/**
 * ETERNITY INTELLIGENCE — ME (PERSONAL/ADMIN ONLY)
 * No company-wide operational data
 */
import React from 'react';
import { motion } from 'framer-motion';
import { User, Briefcase, Users, Shield, Settings, Activity, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';

const ProfileMe = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';

  const theme = {
    bg: '#0A0A0A',
    cardBg: '#141414',
    text: '#F4F2ED',
    textMuted: '#8A8A8A',
    ember: '#FC3B00',
    border: '#2A2A2A'
  };

  const personalItems = [
    { id: 'profile', label: 'Profile', icon: User, path: '/intelligence/me/profile' },
    { id: 'my-work', label: 'My Work', icon: Briefcase, path: '/intelligence/me/work' },
    { id: 'activity', label: 'My Activity', icon: Activity, path: '/intelligence/me/activity' }
  ];

  const adminItems = [
    { id: 'team', label: 'Team', icon: Users, path: '/intelligence/me/team' },
    { id: 'permissions', label: 'Permissions', icon: Shield, path: '/intelligence/me/permissions' }
  ];

  const systemItems = [
    { id: 'settings', label: 'Settings', icon: Settings, path: '/intelligence/settings' }
  ];

  return (
    <div className="min-h-screen px-4 py-5 md:p-8" style={{ backgroundColor: theme.bg, color: theme.text }}>
      <div className="max-w-4xl mx-auto">
        {/* Profile Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-light"
              style={{
                background: 'linear-gradient(135deg, #E85002 0%, #C10801 100%)',
                color: '#FFF'
              }}
            >
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div>
              <h1 className="text-xl font-light">{user?.name || 'User'}</h1>
              <p className="text-sm" style={{ color: theme.textMuted }}>{user?.email}</p>
            </div>
          </div>
          <div
            className="inline-block px-2.5 py-1 rounded-full text-xs font-medium"
            style={{
              backgroundColor: `${theme.ember}20`,
              color: theme.ember
            }}
          >
            {user?.role || 'USER'}
          </div>
        </div>

        {/* Personal Section */}
        <Section title="PERSONAL" items={personalItems} theme={theme} navigate={navigate} />

        {/* Admin Section */}
        {isAdmin && <Section title="ADMIN" items={adminItems} theme={theme} navigate={navigate} />}

        {/* System Section */}
        <Section title="SYSTEM" items={systemItems} theme={theme} navigate={navigate} />

        {/* Logout */}
        <div className="mt-6">
          <button
            onClick={() => {
              authService.logout();
              navigate('/login');
            }}
            className="w-full p-3 rounded-xl border text-sm font-light transition-all hover:border-ember"
            style={{
              backgroundColor: theme.cardBg,
              borderColor: theme.border,
              color: theme.ember
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, items, theme, navigate }) => (
  <div className="mb-5">
    <h2 className="text-[10px] font-medium tracking-wider mb-2" style={{ color: theme.textMuted }}>
      {title}
    </h2>
    <div
      className="rounded-xl overflow-hidden"
      style={{
        backgroundColor: theme.cardBg,
        border: `1px solid ${theme.border}`
      }}
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <div key={item.id}>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onClick={() => navigate(item.path)}
              className="w-full p-3 flex items-center justify-between transition-all hover:bg-opacity-50"
              style={{
                backgroundColor: 'transparent'
              }}
            >
              <div className="flex items-center gap-3">
                <Icon size={18} style={{ color: theme.textMuted }} />
                <span className="text-sm font-light">{item.label}</span>
              </div>
              <ChevronRight size={16} style={{ color: theme.textMuted }} />
            </motion.button>
            {index < items.length - 1 && (
              <div className="h-px mx-3" style={{ backgroundColor: theme.border }} />
            )}
          </div>
        );
      })}
    </div>
  </div>
);

export default ProfileMe;
